/**
 * FILE: packages/utils/src/course.ts
 * PURPOSE: Shared business utility.
 * AGENT_CONTEXT: Pure functions. No React, no Supabase client imports.
 * LAST_REVIEWED: 2026-05-07
 */

export const COURSES: Record<string, string> = {
  CFA_L1: 'CFA Level 1',
  CFA_L2: 'CFA Level 2',
  CFA_L3: 'CFA Level 3',
  FRM_P1: 'FRM Part 1',
  FRM_P2: 'FRM Part 2',
  FM: 'Financial Modeling',
  Financial_Modeling: 'Financial Modeling',
  REV_PACK: 'Revision Pack',
  REV_CFA_L1: 'CFA L1 Revision Pack',
  REV_CFA_L2: 'CFA L2 Revision Pack',
  REV_CFA_L3: 'CFA L3 Revision Pack',
  CDP: 'Career Development Program',
  CHALLENGERS: 'Challengers Pack',
  RETAKER: 'Retaker Pack',
  JuiceNotes: 'Juice Notes',
  COMBO_L1_L2: 'CFA L1 + L2 Combo',
  COMBO_L2_L3: 'CFA L2 + L3 Combo',
  COMBO_L1_L2_L3: 'CFA L1 + L2 + L3 Combo',
}

/** Stored on `students.program` / `enrollments.program` — camelCase, no spaces. */
export const PACKAGES = ['Essential', 'HomeSchool', 'LiveSchool', 'Classroom', 'JuiceNotes'] as const
export type Package = typeof PACKAGES[number]

const PROGRAM_LABELS: Record<string, string> = {
  Essential: 'Essential',
  HomeSchool: 'Home School',
  LiveSchool: 'Live School',
  Classroom: 'Classroom',
  JuiceNotes: 'Juice Notes',
  // legacy rows (until migrated)
  'Home School': 'Home School',
  'Live School': 'Live School',
}

export const PAYMENT_MODES = [
  'UPI',
  'Cash',
  'Card',
  'Cheque',
  'NEFT',
  'RTGS',
  'Bank Transfer',
  'CCAvenue',
  'CCAvenue INV',
  'PayPal',
  'Razorpay',
  'Online Transfer',
  'Other',
] as const
export type PaymentMode = typeof PAYMENT_MODES[number]

export const ATTEMPTS_BY_COURSE: Record<string, string[]> = {
  CFA_L1: ['Feb_26', 'May_26', 'Aug_26', 'Nov_26', 'Feb_27', 'May_27', 'Aug_27', 'Nov_27'],
  CFA_L2: ['May_26', 'Aug_26', 'Nov_26', 'May_27', 'Aug_27', 'Nov_27'],
  CFA_L3: ['Feb_26', 'Aug_26', 'Feb_27', 'Aug_27'],
  FRM_P1: ['May_26', 'Nov_26', 'May_27', 'Nov_27'],
  FRM_P2: ['May_26', 'Nov_26', 'May_27', 'Nov_27'],
  FM: [],
  Financial_Modeling: [],
  REV_PACK: [],
  REV_CFA_L1: ['Feb_26', 'May_26', 'Aug_26', 'Nov_26', 'Feb_27', 'May_27', 'Aug_27', 'Nov_27'],
  REV_CFA_L2: ['May_26', 'Aug_26', 'Nov_26', 'May_27', 'Aug_27', 'Nov_27'],
  REV_CFA_L3: ['Feb_26', 'Aug_26', 'Feb_27', 'Aug_27'],
  CDP: [],
  CHALLENGERS: [],
  RETAKER: [],
  JuiceNotes: [],
  COMBO_L1_L2: ['Feb_26', 'May_26', 'Aug_26', 'Nov_26', 'Feb_27', 'May_27', 'Aug_27', 'Nov_27'],
  COMBO_L2_L3: ['May_26', 'Aug_26', 'Nov_26', 'May_27', 'Aug_27', 'Nov_27'],
  COMBO_L1_L2_L3: ['Feb_26', 'May_26', 'Aug_26', 'Nov_26', 'Feb_27', 'May_27', 'Aug_27', 'Nov_27'],
}

export const COMBO_TYPES = {
  L1_L2: { label: 'CFA L1 + L2', months: 24, startCourse: 'CFA_L1' },
  L2_L3: { label: 'CFA L2 + L3', months: 24, startCourse: 'CFA_L2' },
  L1_L2_L3: { label: 'CFA L1 + L2 + L3', months: 36, startCourse: 'CFA_L1' },
} as const

function detectComboCode(s: string): string | null {
  if (s.includes('l1') && s.includes('l2') && s.includes('l3')) return 'COMBO_L1_L2_L3'
  if (s.includes('l2') && s.includes('l3')) return 'COMBO_L2_L3'
  if (s.includes('l1') && s.includes('l2')) return 'COMBO_L1_L2'
  return null
}

export function normaliseCourse(raw: string): string {
  if (!raw) return ''
  const s = raw.trim().toLowerCase().replace(/[\s_\-]/g, '')
  if (['cfal1', 'cfalevel1', 'cfalvl1'].includes(s)) return 'CFA_L1'
  if (['cfal2', 'cfalevel2', 'cfalvl2'].includes(s)) return 'CFA_L2'
  if (['cfal3', 'cfalevel3', 'cfalvl3'].includes(s)) return 'CFA_L3'
  if (['frmp1', 'frmpart1'].includes(s)) return 'FRM_P1'
  if (['frmp2', 'frmpart2'].includes(s)) return 'FRM_P2'
  if (['fm', 'financialmodeling', 'financialmodelling'].includes(s)) return 'Financial_Modeling'
  if (['revpack', 'revisionpack', 'cfarevisionpack'].includes(s)) return 'REV_PACK'
  if (['revcfal1', 'revisionpackl1', 'cfal1revisionpack'].includes(s)) return 'REV_CFA_L1'
  if (['revcfal2', 'revisionpackl2', 'cfal2revisionpack'].includes(s)) return 'REV_CFA_L2'
  if (['revcfal3', 'revisionpackl3', 'cfal3revisionpack'].includes(s)) return 'REV_CFA_L3'
  if (['cdp', 'careerdevelopmentprogram'].includes(s)) return 'CDP'
  if (['challengers', 'challengerpack', 'challengerspack'].includes(s)) return 'CHALLENGERS'
  if (['retaker', 'retakers', 'retakerpack'].includes(s)) return 'RETAKER'
  if (['juicenotes', 'juicenote', 'juicenotesproduct', 'juice_notes', 'juicenotespack'].includes(s)) return 'JuiceNotes'
  const combo = detectComboCode(s)
  if (combo) return combo
  return raw.trim()
}

/**
 * Expand a normalised course code into the individual course codes it grants access to.
 * Combo packages return the included CFA levels; all other codes return themselves.
 */
export function courseMatchCodes(raw: string | null | undefined): string[] {
  const code = normaliseCourse(raw ?? '')
  if (!code) return []
  switch (code) {
    case 'COMBO_L1_L2':
      return ['CFA_L1', 'CFA_L2']
    case 'COMBO_L2_L3':
      return ['CFA_L2', 'CFA_L3']
    case 'COMBO_L1_L2_L3':
      return ['CFA_L1', 'CFA_L2', 'CFA_L3']
    default:
      return [code]
  }
}

/** Normalise package/program strings to canonical `PACKAGES` codes. */
export function normaliseProgram(raw: string): string {
  if (!raw) return ''
  const t = raw.trim()
  const compact = t.replace(/[\s_\-]/g, '').toLowerCase()
  if (compact === 'essential') return 'Essential'
  if (compact === 'homeschool') return 'HomeSchool'
  if (compact === 'liveschool') return 'LiveSchool'
  if (compact === 'classroom') return 'Classroom'
  if (compact === 'juicenotes' || compact === 'juicenote') return 'JuiceNotes'
  if (t === 'Home School') return 'HomeSchool'
  if (t === 'Live School') return 'LiveSchool'
  return t
}

export function getCourseLabel(code: string | null | undefined): string {
  if (!code) return 'No course'
  return COURSES[code] || code
}

export function getProgramLabel(code: string | null | undefined): string {
  if (!code) return '—'
  return PROGRAM_LABELS[code] ?? code
}
